import { FC, useState, useRef, useEffect } from 'react';
import { FiSearch, FiChevronDown, FiChevronUp, FiCheck, FiLoader, FiX } from 'react-icons/fi';
import { DashboardStatus } from '../../types/memberStatus';
import { VeraScoreRange, QuickSegment } from './SideFilterPanel';

interface TopFilterBarProps {
  filters: {
    search: string;
    status: string[];
    sortBy: string;
    sortOrder: 'asc' | 'desc';
    showLcuOnly: boolean;
    veraScoreRange: VeraScoreRange[];
    hasDashboardData: boolean | null;
    quickSegment: QuickSegment;
  };
  searchInput: string;
  onFilterChange: (filters: TopFilterBarProps['filters']) => void;
  onSearchInputChange: (value: string) => void;
  onSearchSubmit: () => void;
  totalCount: number;
  filteredCount: number;
  isOpen: boolean;
  isSearching?: boolean;
  onClose?: () => void;
}

const TopFilterBar: FC<TopFilterBarProps> = ({
  filters,
  searchInput,
  onFilterChange,
  onSearchInputChange,
  onSearchSubmit,
  totalCount,
  filteredCount,
  isOpen,
  isSearching = false,
  onClose,
}) => {
  const [isStatusOpen, setIsStatusOpen] = useState(false);
  const [isSortOpen, setIsSortOpen] = useState(false);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const statusDropdownRef = useRef<HTMLDivElement>(null);
  const sortDropdownRef = useRef<HTMLDivElement>(null);
  const statusOptions = Object.values(DashboardStatus);
  const sortOptions = [
    { value: 'member_name', label: 'Member Name' },
    { value: 'email', label: 'Email' },
    { value: 'verascore', label: 'VeraScore' },
    { value: 'dashboard_status', label: 'Status' },
    { value: 'last_profile_edit', label: 'Last Updated' },
  ];

  const allStatusesSelected = statusOptions.length === filters.status.length;

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target as Node)) {
        setIsStatusOpen(false);
      }
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target as Node)) {
        setIsSortOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle search input with typing indicator
  const handleSearchChange = (value: string) => {
    // Update the input value immediately for UI responsiveness
    onSearchInputChange(value);

    // Debounce the actual search operation
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
  };

  // Handle Enter key press in search input
  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      onSearchSubmit();
    }
  };

  // Toggle all statuses at once
  const toggleAllStatuses = () => {
    onFilterChange({
      ...filters,
      status: allStatusesSelected ? [] : [...statusOptions],
    });
  };

  if (!isOpen) return null;

  return (
    <div className="w-full overflow-y-auto text-sm" style={{
      background: 'rgba(255, 255, 255, 0.85)',
      backdropFilter: 'blur(10px)',
      WebkitBackdropFilter: 'blur(10px)',
      borderBottom: '1px solid rgba(255, 255, 255, 0.5)',
      boxShadow: '0 4px 20px rgba(31, 38, 135, 0.07)',
      position: 'relative',
      overflow: 'hidden',
      minHeight: '100px',
      zIndex: 50 // Higher z-index to ensure visibility
    }}>
      {/* Mobile-specific visible indicator to enhance visibility */}
      <div className="block sm:hidden w-full h-1 bg-brand-blue opacity-70"></div>
      
      {/* Decorative glassmorphic elements */}
      <div
        style={{
          position: 'absolute',
          top: '-60px',
          right: '-60px',
          width: '120px',
          height: '120px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(59, 130, 246, 0.1) 0%, rgba(59, 130, 246, 0) 70%)',
          filter: 'blur(20px)',
          zIndex: 0,
        }}
      ></div>
      <div
        style={{
          position: 'absolute',
          bottom: '-40px',
          left: '-40px',
          width: '100px',
          height: '100px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(139, 92, 246, 0.1) 0%, rgba(139, 92, 246, 0) 70%)',
          filter: 'blur(20px)',
          zIndex: 0,
        }}
      ></div>
      
      <div className="p-3 pb-4 flex flex-col gap-3 relative z-10 max-w-7xl mx-auto">
        {/* Header with Close Button */}
        <div className="flex items-center justify-between">
          <h2 className="text-base font-medium text-slate-800">Filters</h2>
          {onClose && (
            <button
              onClick={onClose}
              className="p-1 rounded-full hover:bg-white/50 transition-colors"
              aria-label="Close filters"
              style={{
                backdropFilter: 'blur(4px)',
                WebkitBackdropFilter: 'blur(4px)'
              }}
            >
              <FiX size={18} className="text-slate-600" />
            </button>
          )}
        </div>

        {/* Results Count */}
        <div className="flex items-center justify-between gap-2 mb-1">
          <div className="text-xs text-slate-600 bg-white/40 px-2 py-1 rounded-md flex-grow" style={{ 
            backdropFilter: 'blur(4px)',
            WebkitBackdropFilter: 'blur(4px)',
            border: '1px solid rgba(255, 255, 255, 0.3)'
          }}>
            Showing {filteredCount} of {totalCount} members
          </div>
          
          {/* Clear All button - only visible when filters are applied */}
          {(filters.search !== '' || filters.status.length > 0 || filters.sortBy !== 'verascore' ||
            filters.sortOrder !== 'desc' || !filters.showLcuOnly ||
            filters.veraScoreRange.length > 0 || filters.hasDashboardData !== null ||
            filters.quickSegment !== 'none') && (
            <button
              onClick={() => {
                onSearchInputChange('');
                onFilterChange({
                  search: '',
                  status: [],
                  sortBy: 'verascore',
                  sortOrder: 'desc',
                  showLcuOnly: true,
                  veraScoreRange: [],
                  hasDashboardData: null,
                  quickSegment: 'none'
                });
                onSearchSubmit();
              }}
              className="text-xs px-2 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded flex items-center"
              title="Clear all filters"
            >
              <FiX size={12} className="mr-1" />
              Clear
            </button>
          )}
        </div>

        {/* Mobile-friendly layout */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          {/* Search Input */}
          <div className="space-y-1 md:col-span-2">
            <label className="block text-xs font-medium text-slate-700">Search</label>
            <div className="relative">
              <input
                type="text"
                value={searchInput}
                onChange={e => handleSearchChange(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                className="w-full pl-8 pr-3 py-1.5 text-sm rounded-md focus:ring-brand-blue focus:border-brand-blue"
                placeholder="Search members..."
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  backdropFilter: 'blur(4px)',
                  WebkitBackdropFilter: 'blur(4px)',
                  border: '1px solid rgba(255, 255, 255, 0.6)',
                  boxShadow: '0 2px 5px rgba(0, 0, 0, 0.05)'
                }}
              />
              <div className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400">
                {isSearching ? (
                  <FiLoader className="animate-spin" size={14} />
                ) : (
                  <FiSearch size={14} />
                )}
              </div>
              {searchInput && (
                <button
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  onClick={() => {
                    onSearchInputChange('');
                    onFilterChange({
                      ...filters,
                      search: '',
                    });
                    // Automatically trigger search with empty string when clearing
                    onSearchSubmit();
                  }}
                >
                  <FiX size={14} />
                </button>
              )}
            </div>
            <div className="md:flex md:space-x-2">
              <button
                onClick={onSearchSubmit}
                className="w-full mt-1 px-3 py-1.5 bg-blue-500 text-white font-medium rounded hover:bg-blue-600 transition-colors text-xs"
              >
                Search
              </button>
            </div>
          </div>

          {/* Status Filter */}
          <div className="space-y-1">
            <label className="block text-xs font-medium text-slate-700">Status</label>
            <div ref={statusDropdownRef} className="relative">
              <button
                onClick={() => setIsStatusOpen(!isStatusOpen)}
                className="w-full flex items-center justify-between px-3 py-1.5 rounded-md text-sm"
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  backdropFilter: 'blur(4px)',
                  WebkitBackdropFilter: 'blur(4px)',
                  border: '1px solid rgba(255, 255, 255, 0.6)',
                  boxShadow: '0 2px 5px rgba(0, 0, 0, 0.05)'
                }}
              >
                <span className="text-slate-700 truncate">
                  {filters.status.length === 0
                    ? 'All Statuses'
                    : filters.status.length === 1
                      ? filters.status[0]
                      : `${filters.status.length} selected`}
                </span>
                {isStatusOpen ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />}
              </button>

              {isStatusOpen && (
                <div className="absolute z-50 w-full mt-1 rounded-md shadow-lg max-h-48 overflow-y-auto" style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(10px)',
                  WebkitBackdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.6)',
                  boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)'
                }}>
                  <div className="p-1.5 border-b border-slate-200/70">
                    <button
                      onClick={toggleAllStatuses}
                      className="flex items-center w-full px-2 py-1 hover:bg-slate-50/80 rounded text-xs"
                    >
                      <span className="w-5">
                        {allStatusesSelected && <FiCheck size={14} className="text-brand-blue" />}
                      </span>
                      <span>All Statuses</span>
                    </button>
                  </div>
                  <div className="p-1.5">
                    {statusOptions.map(status => (
                      <button
                        key={status}
                        onClick={() => {
                          const newStatus = filters.status.includes(status)
                            ? filters.status.filter(s => s !== status)
                            : [...filters.status, status];

                          onFilterChange({
                            ...filters,
                            status: newStatus,
                          });
                        }}
                        className="flex items-center w-full px-2 py-1 hover:bg-slate-50/80 rounded text-xs"
                      >
                        <span className="w-5">
                          {filters.status.includes(status) && (
                            <FiCheck size={14} className="text-brand-blue" />
                          )}
                        </span>
                        <span>{status}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Sort By Filter */}
          <div className="space-y-1">
            <label className="block text-xs font-medium text-slate-700">Sort By</label>
            <div ref={sortDropdownRef} className="relative">
              <button
                onClick={() => setIsSortOpen(!isSortOpen)}
                className="w-full flex items-center justify-between px-3 py-1.5 rounded-md text-sm"
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  backdropFilter: 'blur(4px)',
                  WebkitBackdropFilter: 'blur(4px)',
                  border: '1px solid rgba(255, 255, 255, 0.6)',
                  boxShadow: '0 2px 5px rgba(0, 0, 0, 0.05)'
                }}
              >
                <span className="text-slate-700 truncate">
                  {sortOptions.find(opt => opt.value === filters.sortBy)?.label || 'Member Name'}
                </span>
                {isSortOpen ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />}
              </button>

              {isSortOpen && (
                <div className="absolute z-50 w-full mt-1 rounded-md shadow-lg" style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(10px)',
                  WebkitBackdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.6)',
                  boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)'
                }}>
                  <div className="p-1.5">
                    {sortOptions.map(option => (
                      <button
                        key={option.value}
                        onClick={() => {
                          const newSortOrder =
                            filters.sortBy === option.value
                              ? filters.sortOrder === 'asc'
                                ? 'desc'
                                : 'asc'
                              : 'asc';

                          onFilterChange({
                            ...filters,
                            sortBy: option.value,
                            sortOrder: newSortOrder,
                          });
                          
                          setIsSortOpen(false);
                        }}
                        className="flex items-center w-full px-2 py-1 hover:bg-slate-50/80 rounded text-xs"
                      >
                        <span className="w-5">
                          {filters.sortBy === option.value && (
                            <FiCheck size={14} className="text-brand-blue" />
                          )}
                        </span>
                        <span>{option.label}</span>
                        {filters.sortBy === option.value && (
                          <span className="ml-auto text-brand-blue">
                            {filters.sortOrder === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* LCU Toggle */}
          <div className="space-y-1">
            <label className="block text-xs font-medium text-slate-700">Member Type</label>
            <div className="flex items-center px-3 py-1.5 rounded-md" style={{
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(4px)',
              WebkitBackdropFilter: 'blur(4px)',
              border: '1px solid rgba(255, 255, 255, 0.6)',
              boxShadow: '0 2px 5px rgba(0, 0, 0, 0.05)'
            }}>
              <input
                type="checkbox"
                id="lcu-toggle-top"
                checked={filters.showLcuOnly}
                onChange={() => {
                  onFilterChange({
                    ...filters,
                    showLcuOnly: !filters.showLcuOnly,
                  });
                }}
                className="h-4 w-4 text-brand-blue focus:ring-brand-blue rounded"
              />
              <label htmlFor="lcu-toggle-top" className="ml-2 text-sm text-slate-700 truncate flex-1">
                LCU Members Only
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TopFilterBar;
